import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'

interface Props {
	isFocusOnAppear?: boolean
	min?: number
	max?: number
	placeholder?: string
	value?: string
	defaultValue?: string | number
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void
	className?: string
	autoFocus?: boolean
	minErrorMessage?: string
	maxErrorMessage?: string
}

const NumberInput: React.FC<Props> = ({ isFocusOnAppear, min, max, placeholder, value, defaultValue, onChange, className, autoFocus, minErrorMessage, maxErrorMessage }) => {
	const htmlInputRef = useRef<HTMLInputElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [hasError, setHasError] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')

	const triggerShake = useCallback(() => {
		const el = containerRef.current
		if (!el) return
		el.style.animation = 'none'
		void el.offsetHeight
		el.style.animation = ''
	}, [])

	useEffect(() => {
		if (htmlInputRef.current && (isFocusOnAppear || autoFocus)) {
			htmlInputRef.current.focus()
		}
	}, [isFocusOnAppear, autoFocus])

	const handleInput = (event: ChangeEvent<HTMLInputElement>): void => {
		const val = event.target.valueAsNumber
		const minVal = min ?? 0
		const maxVal = max ?? 10_000

		if (Number.isNaN(val) || event.target.value === '') {
			setHasError(false)
			setErrorMessage('')
			onChange?.(event)
			return
		}

		if (val < minVal) {
			setHasError(true)
			setErrorMessage(minErrorMessage ?? `${minVal} 이상의 값을 입력해 주세요.`)
			triggerShake()
		} else if (val > maxVal) {
			setHasError(true)
			setErrorMessage(maxErrorMessage ?? `${maxVal} 이하의 값을 입력해 주세요.`)
			triggerShake()
		} else {
			setHasError(false)
			setErrorMessage('')
		}

		onChange?.(event)
	}

	return (
		<Container ref={containerRef} $shake={hasError}>
			<StyledInput
				ref={htmlInputRef}
				type="number"
				pattern="[0-9]*"
				inputMode="numeric"
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize="off"
				placeholder={placeholder}
				value={value}
				defaultValue={defaultValue}
				onChange={handleInput}
				className={className}
				$hasError={hasError}
			/>
			{hasError && <ErrorText>{errorMessage}</ErrorText>}
		</Container>
	)
}

const shake = keyframes`
	0%, 100% { transform: translateX(0); }
	25% { transform: translateX(-3px); }
	50% { transform: translateX(2px); }
	75% { transform: translateX(-1px); }
`

const Container = styled.div<{ $shake: boolean }>`
	display: flex;
	flex-direction: column;
	position: relative;
	${({ $shake }) => $shake && css`
		animation: ${shake} 0.4s ease;
	`}
`

const StyledInput = styled.input<{ $hasError: boolean }>`
	width: 100%;
	height: 48px;
	padding: 14px 16px;
	border: none;
	border-radius: 16px;
	background-color: var(--search-field-bg-color);
	color: var(--text-color);
	font-size: 16px;
	line-height: 1.5;
	outline: none;
	transition: background-color 0.2s ease, border-color 0.2s ease;

	&:focus {
		background-color: var(--input-focus-bg-color);
	}

	&::placeholder {
		color: var(--sub-text-color);
	}

	&:disabled {
		opacity: 0.4;
		cursor: default;
	}

	/* Hide number spinner */
	&::-webkit-outer-spin-button,
	&::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	-moz-appearance: textfield;
`

const ErrorText = styled.span`
	position: absolute;
	top: 100%;
	left: 4px;
	font-size: 13px;
	color: var(--danger);
	margin-top: 2px;
`

export default NumberInput
